import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, MenuItem, Button, ToggleButton, ToggleButtonGroup,
  Alert, Chip, CircularProgress, Stack, InputAdornment, IconButton, Tooltip, Divider,
  List, ListItemButton, ListItemText, Accordion, AccordionSummary, AccordionDetails,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Pagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlaceIcon from '@mui/icons-material/Place';
import MapIcon from '@mui/icons-material/Map';
import ClearIcon from '@mui/icons-material/Clear';
import clientApi from '../services/clientApi';
import MapSearch from '../components/MapSearch';

const Search: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [filters, setFilters] = useState({ comparableTypeID: '1', contractTypeID: '1', marketZoneID: '' });
  const [advancedFilters, setAdvancedFilters] = useState({
    surfaceMin: '', surfaceMax: '', priceMin: '', priceMax: '', rooms: '', bathrooms: '',
    floor: '', energyClass: '', elevator: '', parking: '', balcony: '', garden: '', propertyType: '',
  });

  const [userReference, setUserReference] = useState('');
  const [results, setResults] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Zone search
  const [zoneSearchQuery, setZoneSearchQuery] = useState('');
  const [zoneSearchResults, setZoneSearchResults] = useState<any[]>([]);
  const [searchingZones, setSearchingZones] = useState(false);
  const [selectedZoneName, setSelectedZoneName] = useState('');

  // Geographic search
  const [searchMode, setSearchMode] = useState<'zone' | 'geo'>('zone');
  const [addressQuery, setAddressQuery] = useState('');
  const [addressResults, setAddressResults] = useState<any[]>([]);
  const [searchingAddress, setSearchingAddress] = useState(false);
  const [showAddressResults, setShowAddressResults] = useState(false);
  const [geoLocation, setGeoLocation] = useState({ lat: 44.4938, lng: 11.3387, radius: 1000 });

  useEffect(() => { setIsAuthenticated(clientApi.isAuthenticated()); }, []);

  const handleZoneSearch = async () => {
    if (!zoneSearchQuery.trim()) return;
    try {
      setSearchingZones(true);
      setError('');
      const data = await clientApi.searchMarketZones(zoneSearchQuery);
      setZoneSearchResults(data.results || []);
      if (!data.results || data.results.length === 0) {
        setError(`Nessuna zona trovata per "${zoneSearchQuery}". Prova con il nome di una città (es. "Bologna").`);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Errore nella ricerca delle zone');
    } finally {
      setSearchingZones(false);
    }
  };

  const selectZone = (zoneId: string, zoneName?: string) => {
    setFilters({ ...filters, marketZoneID: zoneId });
    setSelectedZoneName(zoneName || '');
    setZoneSearchResults([]);
  };

  const handleAddressSearch = async () => {
    if (!addressQuery.trim()) return;
    try {
      setSearchingAddress(true);
      setError('');
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(addressQuery)}&format=json&limit=5&countrycodes=it&addressdetails=1`,
        { headers: { 'User-Agent': 'AreaProxima-Frontend/1.0' } }
      );
      const data = await response.json();
      const formatted = data.map((item: any) => ({ display_name: item.display_name, lat: parseFloat(item.lat), lng: parseFloat(item.lon) }));
      setAddressResults(formatted);
      if (formatted.length > 0) setShowAddressResults(true);
      else setError(`Nessun indirizzo trovato per "${addressQuery}"`);
    } catch (err: any) {
      setError('Errore nella ricerca dell\'indirizzo');
    } finally {
      setSearchingAddress(false);
    }
  };

  const selectAddress = (result: any) => {
    setGeoLocation({ lat: result.lat, lng: result.lng, radius: geoLocation.radius });
    setAddressQuery(result.display_name);
    setShowAddressResults(false);
  };

  const handleMapLocationChange = (lat: number, lng: number, radius: number) => {
    setGeoLocation({ lat, lng, radius });
  };

  const handleSearch = async (e?: React.FormEvent, page: number = 1) => {
    if (e) e.preventDefault();
    try {
      setLoading(true);
      setError('');
      const searchFilters: any = {
        comparableTypeID: parseInt(filters.comparableTypeID),
        contractTypeID: parseInt(filters.contractTypeID),
      };
      if (searchMode === 'zone') {
        if (!filters.marketZoneID) {
          setError('Seleziona una zona di mercato (cerca per città) o usa la ricerca geografica.');
          setLoading(false);
          return;
        }
        searchFilters.marketZoneID = filters.marketZoneID;
      } else {
        searchFilters.latitude = geoLocation.lat;
        searchFilters.longitude = geoLocation.lng;
        searchFilters.radius = geoLocation.radius;
      }
      const a = advancedFilters;
      if (a.surfaceMin) searchFilters.surfaceMin = parseFloat(a.surfaceMin);
      if (a.surfaceMax) searchFilters.surfaceMax = parseFloat(a.surfaceMax);
      if (a.priceMin) searchFilters.priceMin = parseFloat(a.priceMin);
      if (a.priceMax) searchFilters.priceMax = parseFloat(a.priceMax);
      if (a.rooms) searchFilters.rooms = parseInt(a.rooms);
      if (a.bathrooms) searchFilters.bathrooms = parseInt(a.bathrooms);
      if (a.floor) searchFilters.floor = parseInt(a.floor);
      if (a.energyClass) searchFilters.energy_class = a.energyClass;
      if (a.elevator) searchFilters.elevator = a.elevator === 'true';
      if (a.parking) searchFilters.parking = a.parking === 'true';
      if (a.balcony) searchFilters.balcony = a.balcony === 'true';
      if (a.garden) searchFilters.garden = a.garden === 'true';
      if (a.propertyType) searchFilters.propertyType = a.propertyType;

      const data = await clientApi.searchByAttribute(searchFilters, userReference || undefined, page, 25);
      setResults(data);
      setCurrentPage(page);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Errore nella ricerca');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(price);

  const pricePerSqm = (item: any): number | null => {
    const price = item.askingPrice || item.price;
    const surface = item.grossSquareFootage || item.surface;
    if (!price || !surface) return null;
    return price / surface;
  };

  const setAdv = (k: string, v: string) => setAdvancedFilters({ ...advancedFilters, [k]: v });
  const totalPages = results?._metadata?.pages?.last || 1;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4">Ricerca Comparabili</Typography>
        <Typography color="text.secondary">Cerca immobili comparabili</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '400px 1fr' }, gap: 3, alignItems: 'start' }}>
        {/* ---------------- Search form ---------------- */}
        <Paper variant="outlined" sx={{ p: 2.5, position: { lg: 'sticky' }, top: 16 }}>
          <form onSubmit={handleSearch}>
            <Stack spacing={2.5}>
              <ToggleButtonGroup
                color="primary" exclusive fullWidth size="small" value={searchMode}
                onChange={(_, v) => v && setSearchMode(v)}
              >
                <ToggleButton value="zone"><MapIcon fontSize="small" sx={{ mr: 0.5 }} />Per zona</ToggleButton>
                <ToggleButton value="geo"><PlaceIcon fontSize="small" sx={{ mr: 0.5 }} />Geografica</ToggleButton>
              </ToggleButtonGroup>

              {/* L'API fornisce solo comparabili di "asking" (offerte): niente
                  scelta del tipo dato. Le compravendite arrivano via KPI
                  Osservatorio (Excel/CSV), non da qui. */}
              <TextField select label="Tipo Contratto" fullWidth value={filters.contractTypeID}
                onChange={(e) => setFilters({ ...filters, contractTypeID: e.target.value })}
                helperText="Comparabili su prezzi richiesti (annunci in offerta)">
                <MenuItem value="1">Vendita</MenuItem>
                <MenuItem value="2">Locazione</MenuItem>
              </TextField>

              {searchMode === 'zone' ? (
                <Box>
                  <TextField
                    label="Zona di mercato" fullWidth placeholder="Cerca per città o CAP (es. Bologna, 40133)"
                    value={zoneSearchQuery}
                    onChange={(e) => setZoneSearchQuery(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleZoneSearch(); } }}
                    slotProps={{ input: { endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleZoneSearch} disabled={searchingZones || !zoneSearchQuery.trim()} edge="end">
                          {searchingZones ? <CircularProgress size={20} /> : <SearchIcon />}
                        </IconButton>
                      </InputAdornment>
                    ) } }}
                  />
                  {filters.marketZoneID && (
                    <Alert severity="success" sx={{ mt: 1.5 }} action={
                      <Button color="inherit" size="small" onClick={() => { setFilters({ ...filters, marketZoneID: '' }); setSelectedZoneName(''); }}>Cambia</Button>
                    }>
                      Zona: <strong>{selectedZoneName || `Zona ${filters.marketZoneID}`}</strong> (ID {filters.marketZoneID})
                    </Alert>
                  )}
                  {zoneSearchResults.length > 0 && (
                    <Paper variant="outlined" sx={{ mt: 1.5, maxHeight: 260, overflow: 'auto' }}>
                      <List dense disablePadding>
                        {zoneSearchResults.map((zone: any, i: number) => {
                          const zoneId = zone.marketZoneID || zone.omiZoneID || zone.id;
                          const zoneName = zone.marketZone || zone.omiZone || zone.name || zone.label || `Zona ${zoneId}`;
                          const parent = zone.marketParentZone ? ` (${zone.marketParentZone})` : '';
                          const type = zone.type === 'market_zone' ? 'Market Zone' : zone.type === 'omi_zone' ? 'OMI Zone' : '';
                          return (
                            <ListItemButton key={`${zoneId}-${i}`} onClick={() => selectZone(zoneId, `${zoneName}${parent}`)}>
                              <ListItemText
                                primary={`${zoneName}${parent}`}
                                secondary={`${zone.municipality ? zone.municipality + ' · ' : ''}${type ? type + ' · ' : ''}ID ${zoneId}`}
                              />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </Paper>
                  )}
                  <TextField
                    label="Market Zone ID (manuale)" size="small" sx={{ mt: 1.5, maxWidth: 220 }}
                    value={filters.marketZoneID}
                    onChange={(e) => { setFilters({ ...filters, marketZoneID: e.target.value }); setSelectedZoneName(''); }}
                  />
                </Box>
              ) : (
                <Box>
                  <TextField
                    label="Cerca indirizzo" fullWidth placeholder="es. Via Rizzoli, Bologna"
                    value={addressQuery}
                    onChange={(e) => setAddressQuery(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddressSearch(); } }}
                    slotProps={{ input: { endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleAddressSearch} disabled={searchingAddress || !addressQuery.trim()} edge="end">
                          {searchingAddress ? <CircularProgress size={20} /> : <SearchIcon />}
                        </IconButton>
                      </InputAdornment>
                    ) } }}
                  />
                  {showAddressResults && addressResults.length > 0 && (
                    <Paper variant="outlined" sx={{ mt: 1, maxHeight: 180, overflow: 'auto' }}>
                      <List dense disablePadding>
                        {addressResults.map((r: any, i: number) => (
                          <ListItemButton key={i} onClick={() => selectAddress(r)}>
                            <ListItemText primary={r.display_name} secondary={`${r.lat.toFixed(5)}, ${r.lng.toFixed(5)}`} />
                          </ListItemButton>
                        ))}
                      </List>
                    </Paper>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Puoi anche cliccare sulla mappa o trascinare il marker.
                  </Typography>
                  <Box sx={{ mt: 1.5 }}>
                    <MapSearch onLocationChange={handleMapLocationChange} initialLat={geoLocation.lat} initialLng={geoLocation.lng} initialRadius={geoLocation.radius} />
                  </Box>
                </Box>
              )}

              <TextField label="User Reference (opzionale)" fullWidth value={userReference}
                onChange={(e) => setUserReference(e.target.value)} helperText="Per il tracking nelle statistiche" />

              <Accordion variant="outlined" disableGutters sx={{ '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>Filtri avanzati</AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 2 }}>
                    <TextField label="Superficie min" type="number" size="small" value={advancedFilters.surfaceMin} onChange={(e) => setAdv('surfaceMin', e.target.value)} />
                    <TextField label="Superficie max" type="number" size="small" value={advancedFilters.surfaceMax} onChange={(e) => setAdv('surfaceMax', e.target.value)} />
                    <TextField label="Prezzo min" type="number" size="small" value={advancedFilters.priceMin} onChange={(e) => setAdv('priceMin', e.target.value)} />
                    <TextField label="Prezzo max" type="number" size="small" value={advancedFilters.priceMax} onChange={(e) => setAdv('priceMax', e.target.value)} />
                    <TextField label="Locali" type="number" size="small" value={advancedFilters.rooms} onChange={(e) => setAdv('rooms', e.target.value)} />
                    <TextField label="Bagni" type="number" size="small" value={advancedFilters.bathrooms} onChange={(e) => setAdv('bathrooms', e.target.value)} />
                    <TextField label="Piano" type="number" size="small" value={advancedFilters.floor} onChange={(e) => setAdv('floor', e.target.value)} />
                    <TextField select label="Classe energ." size="small" value={advancedFilters.energyClass} onChange={(e) => setAdv('energyClass', e.target.value)}>
                      <MenuItem value="">Qualsiasi</MenuItem>
                      {['A+', 'A', 'B', 'C', 'D', 'E', 'F', 'G'].map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </TextField>
                    <TextField select label="Ascensore" size="small" value={advancedFilters.elevator} onChange={(e) => setAdv('elevator', e.target.value)}>
                      <MenuItem value="">Qualsiasi</MenuItem><MenuItem value="true">Sì</MenuItem><MenuItem value="false">No</MenuItem>
                    </TextField>
                    <TextField select label="Posto auto" size="small" value={advancedFilters.parking} onChange={(e) => setAdv('parking', e.target.value)}>
                      <MenuItem value="">Qualsiasi</MenuItem><MenuItem value="true">Sì</MenuItem><MenuItem value="false">No</MenuItem>
                    </TextField>
                    <TextField select label="Balcone" size="small" value={advancedFilters.balcony} onChange={(e) => setAdv('balcony', e.target.value)}>
                      <MenuItem value="">Qualsiasi</MenuItem><MenuItem value="true">Sì</MenuItem><MenuItem value="false">No</MenuItem>
                    </TextField>
                    <TextField select label="Giardino" size="small" value={advancedFilters.garden} onChange={(e) => setAdv('garden', e.target.value)}>
                      <MenuItem value="">Qualsiasi</MenuItem><MenuItem value="true">Sì</MenuItem><MenuItem value="false">No</MenuItem>
                    </TextField>
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Button type="submit" variant="contained" size="large" startIcon={<SearchIcon />}
                disabled={loading || !isAuthenticated} fullWidth>
                {loading ? 'Ricerca in corso…' : 'Cerca Comparables'}
              </Button>
            </Stack>
          </form>
        </Paper>

        {/* ---------------- Results ---------------- */}
        <Box>
          {!results && !loading && (
            <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
              <SearchIcon sx={{ fontSize: 48, opacity: 0.4 }} />
              <Typography sx={{ mt: 1 }}>Imposta i parametri e avvia una ricerca per vedere i comparabili.</Typography>
            </Paper>
          )}

          {loading && !results && (
            <Paper variant="outlined" sx={{ p: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress /><Typography color="text.secondary">Ricerca in corso…</Typography>
            </Paper>
          )}

          {results && (
            <Paper variant="outlined">
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  {results._metadata?.total_count || 0} immobili trovati
                </Typography>
                {results._wrapper && (
                  <Chip size="small" color={results._wrapper.cache_hit ? 'success' : 'info'}
                    label={`${results._wrapper.cache_hit ? 'Da cache' : 'Aggiornato'} · ${results._wrapper.response_time_ms} ms`} />
                )}
                <Tooltip title="Pulisci risultati">
                  <IconButton size="small" onClick={() => { setResults(null); setCurrentPage(1); }}><ClearIcon fontSize="small" /></IconButton>
                </Tooltip>
              </Box>
              <Divider />
              {loading && <Box sx={{ px: 2, py: 1 }}><Typography variant="body2" color="text.secondary">Aggiornamento…</Typography></Box>}

              {results.items && results.items.length > 0 ? (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Tipologia</TableCell>
                          <TableCell>Indirizzo</TableCell>
                          <TableCell align="right">Superficie</TableCell>
                          <TableCell align="right">Prezzo</TableCell>
                          <TableCell align="right">€/m²</TableCell>
                          <TableCell>Data</TableCell>
                          <TableCell align="center">Classe</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {results.items.map((item: any) => {
                          const ppsqm = pricePerSqm(item);
                          const price = item.askingPrice || item.price;
                          const surface = item.grossSquareFootage || item.surface;
                          const ec = item.energyClass;
                          const ecColor = ec ? (['A', 'A+', 'B'].includes(ec) ? 'success' : ['C', 'D'].includes(ec) ? 'warning' : 'error') : 'default';
                          return (
                            <TableRow key={item.uuid || item.id} hover>
                              <TableCell>{item.propertyType || '—'}</TableCell>
                              <TableCell sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.address || '—'}</TableCell>
                              <TableCell align="right">{surface ? `${surface} m²` : '—'}</TableCell>
                              <TableCell align="right"><strong>{price ? formatPrice(price) : '—'}</strong></TableCell>
                              <TableCell align="right">{ppsqm ? `${formatPrice(ppsqm)}/m²` : '—'}</TableCell>
                              <TableCell>{item.salesDate ? new Date(item.salesDate).toLocaleDateString('it-IT') : '—'}</TableCell>
                              <TableCell align="center">{ec ? <Chip size="small" color={ecColor as any} label={ec} /> : '—'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <Pagination color="primary" count={totalPages} page={currentPage} siblingCount={1}
                        disabled={loading} onChange={(_, p) => handleSearch(undefined, p)} />
                    </Box>
                  )}
                </>
              ) : (
                <Box sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography>Nessun immobile trovato per questi criteri.</Typography>
                  <Typography variant="body2">Prova a cambiare zona, tipo di contratto o ad allentare i filtri avanzati.</Typography>
                </Box>
              )}
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Search;
